import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { BLOCK_KPI_MODAL_CONFIG, schoolStatusMeta } from "@/utils/blockDetailDerived";
import "./UsmRiskSchoolsModal.css";

const PAGE_SIZE = 10;
const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");
const pct = (v) => (typeof v === "number" ? `${v.toFixed(1)}%` : "—");

const formatSummary = (metric, block, schools, rows) => {
  if (!block) return null;
  if (metric === "total_schools") {
    const active = schools.filter((s) => (s.total_students ?? 0) > 0).length;
    return (
      <>
        <b>{fmt(block.total_schools)}</b> schools in block · <b>{fmt(active)}</b> active in school scope
      </>
    );
  }
  if (metric === "total_students") {
    const enrolled = rows.reduce((sum, s) => sum + (s.total_students ?? 0), 0);
    return (
      <>
        <b>{fmt(block.total_students)}</b> students in block · <b>{fmt(enrolled)}</b> across listed schools
      </>
    );
  }
  if (metric === "aadhaar_compliance") {
    const below90 = rows.filter((s) => (s.aadhaar_percentage ?? 0) < 90).length;
    return (
      <>
        Block Aadhaar <b>{pct(block.aadhaar_percentage ?? 0)}</b>
        {below90 > 0 ? (
          <>
            {" "}
            · <b>{fmt(below90)}</b> schools below 90%
          </>
        ) : (
          <> · all listed schools at or above 90%</>
        )}
      </>
    );
  }
  if (metric === "critical_schools") {
    return (
      <>
        <b>{fmt(rows.length)}</b> critical schools · block OSH <b>{(block.shi_score ?? 0).toFixed(1)}</b>
      </>
    );
  }
  return null;
};

const BlockKpiSchoolsModal = ({
  open,
  metric,
  onClose,
  schools = [],
  block,
  scopeLabel = "Block scope",
}) => {
  const navigate = useNavigate();
  const config = metric ? BLOCK_KPI_MODAL_CONFIG[metric] : null;
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (open && metric) {
      setQuery("");
      setPage(1);
    }
  }, [open, metric]);

  const rows = useMemo(() => {
    if (!config) return [];
    return config.filterSchools(schools);
  }, [config, schools]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (s) =>
        String(s.school_name || "").toLowerCase().includes(q) ||
        String(s.udise_code || "").includes(q) ||
        String(s.school_category || s.school_management || "").toLowerCase().includes(q),
    );
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!open || !metric || !config) return null;

  const title = config.title;
  const summary = formatSummary(metric, block, schools, rows);

  return createPortal(
    <div className="usm-risk-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="usm-risk-modal usm-risk-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="block-kpi-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="usm-risk-modal-head">
          <div>
            <p className="usm-risk-modal-eyebrow">Block drill-down · {scopeLabel}</p>
            <h3 id="block-kpi-modal-title">{title}</h3>
            <p className="usm-risk-modal-sub">
              {summary}
              {config.description ? <> · {config.description}</> : null}
            </p>
          </div>
          <button type="button" className="usm-risk-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="usm-risk-modal-toolbar usm-risk-modal-toolbar-filters">
          <span className="usm-risk-modal-count">
            Showing <b>{fmt(filtered.length)}</b> of <b>{fmt(rows.length)}</b> schools
          </span>
        </div>

        <div className="usm-risk-modal-body">
          {filtered.length === 0 ? (
            <div className="usm-risk-modal-empty">No schools match this KPI in the current block scope.</div>
          ) : (
            <>
              <div className="usm-risk-modal-body-search">
                <input
                  type="search"
                  className="usm-risk-modal-search"
                  placeholder="Search school name, UDISE, or category..."
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Search schools"
                />
              </div>
              {pageRows.length === 0 ? (
                <div className="usm-risk-modal-empty">No schools match your search.</div>
              ) : (
                <table className="usm-risk-modal-table usm-risk-modal-table-wide">
                  <thead>
                    <tr>
                      <th>School</th>
                      <th>UDISE</th>
                      <th>Category</th>
                      <th>Students</th>
                      <th>Teachers</th>
                      <th>Aadhaar %</th>
                      <th>OSH</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((school) => {
                      const status = schoolStatusMeta(school.rag_status);
                      const aadhaar = school.aadhaar_percentage ?? 0;
                      let action = "Monitor";
                      if (metric === "critical_schools") action = "Weekly OSH review";
                      else if (metric === "aadhaar_compliance" && aadhaar < 90) action = "Close Aadhaar gap";
                      else if (metric === "total_students" && (school.total_students ?? 0) >= 500) action = "High enrolment watch";
                      else if (metric === "total_schools" && (school.total_students ?? 0) === 0) action = "Validate inactive school";

                      return (
                        <tr
                          key={school.udise_code}
                          className="usm-risk-modal-row"
                          onClick={() => {
                            onClose();
                            navigate(`/school/${school.udise_code}`);
                          }}
                        >
                          <td>{school.school_name}</td>
                          <td>{school.udise_code}</td>
                          <td>{school.school_category || school.school_management || "—"}</td>
                          <td>{fmt(school.total_students)}</td>
                          <td>{fmt(school.total_teachers)}</td>
                          <td>{pct(aadhaar)}</td>
                          <td>{typeof school.shi_score === "number" ? school.shi_score.toFixed(1) : "—"}</td>
                          <td>
                            <span className={`blv-badge ${status.className}`}>{status.label}</span>
                          </td>
                          <td>{action}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {filtered.length > 0 && (
          <div className="usm-risk-modal-footer">
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              ‹
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default BlockKpiSchoolsModal;
